import { getTranslationKeys } from "./arb";
import {
  LOCALES,
  STATUSES,
  type ArbDocument,
  type EntryMetadata,
  type JsonObject,
  type JsonValue,
  type Locale,
  type MetadataFile,
  type TranslationStatus,
} from "./types";

const WB_EXTENSION = "x-wb";
const WB_FIELDS = new Set(["note", "tags", "characterLimit", "status"]);

function assertObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be a JSON object.`);
  }
}

function parseWorkflowFields(key: string, raw: unknown): Omit<EntryMetadata, "description"> {
  if (raw === undefined) return {};
  assertObject(raw, `@${key}.${WB_EXTENSION}`);
  const unknown = Object.keys(raw).filter((field) => !WB_FIELDS.has(field));
  if (unknown.length) throw new Error(`@${key}.${WB_EXTENSION} has unsupported field(s): ${unknown.join(", ")}.`);

  const entry: Omit<EntryMetadata, "description"> = {};
  if (raw.note !== undefined) {
    if (typeof raw.note !== "string") throw new Error(`@${key}.${WB_EXTENSION}.note must be a string.`);
    entry.note = raw.note;
  }
  if (raw.tags !== undefined) {
    if (!Array.isArray(raw.tags) || raw.tags.some((tag) => typeof tag !== "string")) {
      throw new Error(`@${key}.${WB_EXTENSION}.tags must be an array of strings.`);
    }
    entry.tags = normalizeTags(raw.tags as string[]);
  }
  if (raw.characterLimit !== undefined) {
    if (raw.characterLimit !== null && (!Number.isInteger(raw.characterLimit) || Number(raw.characterLimit) <= 0)) {
      throw new Error(`@${key}.${WB_EXTENSION}.characterLimit must be a positive integer or null.`);
    }
    entry.characterLimit = raw.characterLimit as number | null;
  }
  if (raw.status !== undefined) {
    assertObject(raw.status, `@${key}.${WB_EXTENSION}.status`);
    const status: Partial<Record<Locale, TranslationStatus>> = {};
    for (const [locale, candidate] of Object.entries(raw.status)) {
      if (!LOCALES.includes(locale as Locale)) throw new Error(`@${key}.${WB_EXTENSION}.status contains unsupported locale ${locale}.`);
      if (!STATUSES.includes(candidate as TranslationStatus)) throw new Error(`@${key}.${WB_EXTENSION}.status.${locale} is invalid.`);
      status[locale as Locale] = candidate as TranslationStatus;
    }
    entry.status = status;
  }
  return entry;
}

export function extractMetadataFromArb(document: ArbDocument): MetadataFile {
  const entries: Record<string, EntryMetadata> = {};
  for (const [arbKey, raw] of Object.entries(document.data)) {
    if (!arbKey.startsWith("@") || arbKey.startsWith("@@")) continue;
    const key = arbKey.slice(1);
    if (!key) throw new Error(`${document.filename} contains empty ARB metadata key "@".`);
    assertObject(raw, `${document.filename} metadata ${arbKey}`);
    const entry: EntryMetadata = { ...parseWorkflowFields(key, raw[WB_EXTENSION]) };
    if (raw.description !== undefined) {
      if (typeof raw.description !== "string") throw new Error(`${arbKey}.description must be a string.`);
      entry.description = raw.description;
    }
    if (Object.keys(entry).length) entries[key] = entry;
    else if (!getTranslationKeys(document).includes(key)) entries[key] = {};
  }
  return { schemaVersion: 1, entries };
}

function workflowObject(entry: EntryMetadata): JsonObject | null {
  const workflow: JsonObject = {};
  if (entry.note) workflow.note = entry.note;
  if (entry.tags?.length) workflow.tags = entry.tags;
  if (entry.characterLimit !== undefined && entry.characterLimit !== null) workflow.characterLimit = entry.characterLimit;
  if (entry.status && Object.keys(entry.status).length) workflow.status = entry.status as unknown as JsonObject;
  return Object.keys(workflow).length ? workflow : null;
}

export function applyMetadataToArb(document: ArbDocument, metadata: MetadataFile): void {
  const metadataKeys = new Set<string>();
  for (const key of Object.keys(document.data)) {
    if (key.startsWith("@") && !key.startsWith("@@")) metadataKeys.add(key.slice(1));
  }
  for (const key of Object.keys(metadata.entries)) metadataKeys.add(key);

  for (const key of metadataKeys) {
    const arbKey = `@${key}`;
    const existing = document.data[arbKey];
    const raw: JsonObject = typeof existing === "object" && existing !== null && !Array.isArray(existing)
      ? structuredClone(existing) as JsonObject
      : {};
    delete raw.description;
    delete raw[WB_EXTENSION];

    const entry = metadata.entries[key];
    if (entry?.description) raw.description = entry.description;
    if (entry) {
      const workflow = workflowObject(entry);
      if (workflow) raw[WB_EXTENSION] = workflow;
    }

    if (Object.keys(raw).length) document.data[arbKey] = raw;
    else delete document.data[arbKey];
  }
}

export function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const tag = raw.trim();
    const identity = tag.toLocaleLowerCase();
    if (!tag || seen.has(identity)) continue;
    seen.add(identity);
    result.push(tag);
  }
  return result;
}

export function getStatus(metadata: MetadataFile, key: string, locale: Locale): TranslationStatus {
  return metadata.entries[key]?.status?.[locale] ?? "new";
}

export function getEntryMetadata(metadata: MetadataFile, key: string): EntryMetadata {
  return metadata.entries[key] ?? {};
}

export function cloneMetadata(metadata: MetadataFile): MetadataFile {
  return structuredClone(metadata) as MetadataFile;
}

export function isMetadataJsonValue(value: unknown): value is JsonValue {
  if (value === null || ["string", "number", "boolean"].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every(isMetadataJsonValue);
  return typeof value === "object" && Object.values(value as Record<string, unknown>).every(isMetadataJsonValue);
}
